<template>
  <v-dialog v-model="dialogVisible" max-width="480px">
    <v-card class="cl-dialog-card">
      <v-card-title>Fork this session</v-card-title>

      <!-- Step 1: name the copy -->
      <v-card-text v-if="!quote">
        <p class="fork-lede">
          Make your own copy of <strong>{{ displayName }}</strong> to build on.
          The original stays as it is, and your fork credits it.
        </p>
        <v-text-field
          v-model="newName"
          label="Name for your fork"
          variant="outlined"
          density="compact"
          :error-messages="error"
          @keyup.enter="fork()"
        />
      </v-card-text>

      <!-- Step 2 (402): paid loops to license first -->
      <v-card-text v-else>
        <p class="fork-lede">
          Some loops in this session are paid. License them to fork it{{
            quote.total_usd ? ` (from $${quote.total_usd.toFixed(2)})` : ""
          }}.
        </p>
        <ul class="fork-buylist">
          <li v-for="row in quote.missing" :key="row.loop_hash" class="fork-row">
            <div class="fork-row-meta">
              <div class="fork-row-name">{{ row.name || "Untitled loop" }}</div>
              <div class="fork-row-sub">
                by {{ row.creator }} · {{ row.license_type }}
                <template v-if="row.min_price_usd > 0">
                  · from ${{ row.min_price_usd.toFixed(2) }}
                </template>
              </div>
              <div v-if="!row.purchasable" class="fork-row-blocked">
                {{ blockedReason(row.reason) }}
              </div>
            </div>
            <v-btn
              v-if="row.purchasable"
              class="cl-btn cl-btn--primary"
              variant="outlined"
              size="small"
              @click="license(row)"
            >
              License
            </v-btn>
          </li>
        </ul>
        <p v-if="error" class="fork-error">{{ error }}</p>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn class="cl-btn cl-btn--ghost" variant="text" @click="close()">Cancel</v-btn>
        <v-btn
          class="cl-btn cl-btn--primary"
          variant="outlined"
          :loading="loading"
          :disabled="!quote && !newName.trim()"
          @click="fork()"
        >
          {{ quote ? "Try again" : "Fork" }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Sibling, not nested: it's its own dialog. -->
  <PurchaseDialog
    v-if="buying"
    v-model:show="showPurchase"
    :loop-hash="buying.loop_hash"
    :loop-name="buying.name || 'Untitled loop'"
    :seller-username="buying.creator"
    :min-price="checkoutMinOf(buying)"
    :suggested-price="buying.suggested_price_usd"
  />
</template>

<script>
import cloudloop from "@/mixins/cloudloop";
import PurchaseDialog from "@/components/PurchaseDialog.vue";
import { checkoutMinOf } from "@/kit/sale.js";

// A fork paused for a PayPal checkout. PurchaseSuccess reads it to offer
// "Back to fork"; the session page reopens this dialog with the same
// request id, so the retry can't create a second fork.
export const PENDING_FORK_KEY = "pendingFork";
const PENDING_FORK_TTL_MS = 60 * 60 * 1000;

export function readPendingFork() {
  try {
    const p = JSON.parse(window.localStorage.getItem(PENDING_FORK_KEY));
    if (p && p.session && Date.now() - p.at < PENDING_FORK_TTL_MS) return p;
  } catch (e) {
    /* missing or unreadable — no pending fork */
  }
  return null;
}

function clearPendingFork() {
  try {
    window.localStorage.removeItem(PENDING_FORK_KEY);
  } catch (e) {
    /* storage unavailable */
  }
}

function newRequestId() {
  if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export default {
  name: "ForkDialog",
  components: { PurchaseDialog },
  mixins: [cloudloop],
  props: {
    show: Boolean,
    sessionName: { type: String, required: true },
    displayName: { type: String, default: "" },
  },
  emits: ["update:show", "forked"],
  data() {
    return {
      newName: "",
      requestId: null,
      quote: null, // 402 payload: { missing, unavailable, total_usd }
      loading: false,
      error: "",
      buying: null,
      showPurchase: false,
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.show;
      },
      set(value) {
        this.$emit("update:show", value);
      },
    },
  },
  watch: {
    show(open) {
      if (!open) return;
      const pending = readPendingFork();
      if (pending && pending.session === this.sessionName) {
        // Back from a checkout: resume the same attempt and re-check.
        this.newName = pending.name;
        this.requestId = pending.requestId;
        this.fork();
      } else {
        this.newName = `${this.sessionName} (fork)`;
        this.requestId = newRequestId();
        this.quote = null;
        this.error = "";
      }
    },
  },
  methods: {
    // The buy sheet's floor is the checkout minimum, not the raw minimum
    // (a suggested price alone still charges a cent; S7).
    checkoutMinOf,
    close() {
      this.dialogVisible = false;
    },
    blockedReason(reason) {
      return {
        "seller-unlinked": "The creator can't take payments yet.",
        "custom-terms": "Custom license: contact the creator.",
        "capture-settling": "Your payment is still settling. Try again shortly.",
      }[reason] || "Not available to license right now.";
    },
    license(row) {
      // Remember the fork across the PayPal redirect.
      try {
        window.localStorage.setItem(
          PENDING_FORK_KEY,
          JSON.stringify({
            session: this.sessionName,
            name: this.newName,
            requestId: this.requestId,
            at: Date.now(),
          }),
        );
      } catch (e) {
        /* storage unavailable — the user can still come back manually */
      }
      this.buying = row;
      this.showPurchase = true;
    },
    async fork() {
      if (this.loading) return;
      this.loading = true;
      this.error = "";
      try {
        const { status, body } = await this.forkSession(this.sessionName, {
          newName: this.newName.trim(),
          requestId: this.requestId,
        });
        const results = body?.data?.results;
        if (status === 200 && results?.session?.name) {
          clearPendingFork();
          this.close();
          // The session lists put the copy at the top of Mine.
          this.$emit("forked", results.session);
          this.$router.push(`/session/${encodeURIComponent(results.session.name)}`);
        } else if (status === 402 && results) {
          this.quote = results;
        } else {
          this.error = body?.message || "Couldn't fork this session. Try again.";
        }
      } catch (e) {
        this.error = "Network error. Check your connection and try again.";
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.fork-lede {
  font-size: var(--font-sm);
  color: var(--text-muted);
  margin: 0 0 0.9rem;
}

.fork-buylist {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.fork-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.fork-row-meta {
  min-width: 0;
}

.fork-row-name {
  font-weight: 600;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.fork-row-sub,
.fork-row-blocked {
  font-size: var(--font-sm);
  color: var(--text-muted);
}

.fork-row-blocked {
  font-style: italic;
}

.fork-error {
  font-size: var(--font-sm);
  color: rgb(var(--v-theme-error));
  margin: 0.75rem 0 0;
}
</style>
